import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'
import type { CronAnswer } from './cronAnswer'

/**
 * Settings → Easeedesk → Auto change status: amandoua ecranele.
 *
 * Perechile vechi: `settings/easeedesk/auto_change_status`,
 * `auto_change_status_list`, `get_workflow_next_status`,
 * `add_auto_change_status`, `activate_auto_change_status`,
 * `remove_auto_change_status`, `rebuild_auto_change_status_configuration`,
 * `trigger_change_status`, `triggers_list`, `create_change_status_trigger`,
 * `update_change_status_trigger`, `change_trigger_status`, `remove_trigger`.
 *
 * `apiGet` / `apiPost` pun ELE `/api/v2` in fata caii.
 */

export interface AutoChangeRule {
  ID: number
  ticket_type: number
  type_name: string
  short_name: string
  color_identifier: string
  status_from: number
  status_from_name: string
  status_to: number
  status_to_name: string
  pending_time: number
  pending_type: string
  active: number
}

export interface AutoChangeShell {
  title: string
  moduleUrl: string
  ticketTypes: { ID: number; type_name: string }[]
  statuses: { ID: number; status_name: string }[]
  pending_types: string[]
}

export interface TriggerRow {
  id: number
  event_type: string
  ticket_type: number
  type_name: string
  to_status: number
  status_name: string
  active: number
}

export interface TriggerShell {
  title: string
  moduleUrl: string
  ticketTypes: { ID: number; type_name: string }[]
  statuses: { ID: number; status_name: string }[]
  events: string[]
}

export function autoChangeShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'auto_change_status'],
    queryFn: () => apiGet<AutoChangeShell>('/settings/easeedesk/auto_change_status'),
  })
}

export function autoChangeListQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'auto_change_status', 'list'],
    queryFn: () => apiGet<{ settings: AutoChangeRule[] }>('/settings/easeedesk/auto_change_status/list'),
  })
}

/** Casuta „To status" se umple din fluxul tipului, ca in portal. */
export function nextStatusesQuery(ticket_type: number, status_from: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'auto_change_status', 'next', ticket_type, status_from],
    queryFn: () =>
      apiGet<{ statuses: { ID: number; status_name: string }[] }>(
        `/settings/easeedesk/auto_change_status/next_status?ticket_type=${ticket_type}&status_from=${status_from}`,
      ),
    enabled: ticket_type > 0 && status_from > 0,
  })
}

/**
 * Casuta „From status" se umple din fluxul tipului ales, ca in portal
 * (`get_ticket_type_status`). `statuses` din cap sunt starile implicite ale
 * furnizorului, nu ale tipului.
 */
export function autoChangeTypeStatusesQuery(ticket_type: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'auto_change_status', 'type_statuses', ticket_type],
    queryFn: () =>
      apiGet<{ statuses: { ID: number; status_name: string }[] }>(
        `/settings/easeedesk/auto_change_status/types/${ticket_type}/statuses`,
      ),
    enabled: ticket_type > 0,
  })
}

export function createAutoChangeRule(
  ticket_type: number,
  status_from: number,
  status_to: number,
  pending_time: number,
  pending_type: string,
) {
  return apiPost<{ ok?: boolean; ID?: number }>('/settings/easeedesk/auto_change_status', {
    ticket_type,
    status_from,
    status_to,
    pending_time,
    pending_type,
  })
}

export function changeAutoChangeRuleStatus(rule: number, on: boolean) {
  return apiPost<{ ok?: boolean }>(`/settings/easeedesk/auto_change_status/${rule}/status`, {
    active: on ? 'on' : 'off',
  })
}

export function removeAutoChangeRule(rule: number) {
  return apiPost<{ ok?: boolean }>(`/settings/easeedesk/auto_change_status/${rule}/remove`, {})
}

export function rebuildAutoChangeCron() {
  return apiPost<CronAnswer>('/settings/easeedesk/auto_change_status/rebuild_cron', {})
}

export function triggerShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'trigger_change_status'],
    queryFn: () => apiGet<TriggerShell>('/settings/easeedesk/trigger_change_status'),
  })
}

export function triggersListQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'trigger_change_status', 'list'],
    queryFn: () => apiGet<{ triggers: TriggerRow[] }>('/settings/easeedesk/trigger_change_status/list'),
  })
}

export function createTrigger(event: string, ticket_type: number, to_status: number) {
  return apiPost<{ ok?: boolean; id?: number }>('/settings/easeedesk/trigger_change_status', {
    event,
    ticket_type,
    to_status,
  })
}

export function updateTrigger(trigger: number, event: string, ticket_type: number, to_status: number) {
  return apiPost<{ ok?: boolean }>(`/settings/easeedesk/trigger_change_status/${trigger}`, {
    event,
    ticket_type,
    to_status,
  })
}

export function changeTriggerStatus(trigger: number, on: boolean) {
  return apiPost<{ ok?: boolean }>(`/settings/easeedesk/trigger_change_status/${trigger}/status`, {
    status: on ? 'true' : 'false',
  })
}

export function removeTrigger(trigger: number) {
  return apiPost<{ ok?: boolean }>(`/settings/easeedesk/trigger_change_status/${trigger}/remove`, {})
}
